import React, { useEffect, useState } from "react";
import {
  Col,
  Row,
  Typography,
  Checkbox,
  Button,
  Form,
  Input,
  Radio,
  Space,
} from "antd";
import { PaperClipOutlined, FilePdfOutlined } from "@ant-design/icons";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { useParams } from "react-router-dom";
import { ConnectedProps } from "react-redux";

import {
  ROLES,
  getFileName,
  responseDetailsFormLayout as layout,
} from "../../../../utils";
import connector from "./Container";
import {
  InsuranceOfferResponseType,
  ResponseDetailsFormType,
} from "../../../../models";

const InsuranceResponseInfo = ({
  actions,
  state,
}: ConnectedProps<typeof connector>) => {
  const { currentOfferResponse, currentOfferRequest, loggedUser } = state;
  // Banka has the same permissions as S-Leasing.
  const isLeasingRole =
    loggedUser?.role === ROLES.S_LEASING_ADMIN ||
    loggedUser?.role === ROLES.BANKA;
  const { offerResponseId } = useParams();
  // null = not rated yet (the DB "correct" column is true / false / null).
  const [rating, setRating] = useState<"correct" | "incorrect" | null>(null);
  const [incorrectReason, setIncorrectReason] = useState("");
  const [isAcceptedChecked, setIsAcceptedChecked] = useState(false);
  const [form] = Form.useForm<ResponseDetailsFormType>();

  useEffect(() => {
    if (!offerResponseId) {
      return;
    }

    actions.getOfferResponseById(offerResponseId);
  }, [offerResponseId]);

  useEffect(() => {
    if (!currentOfferResponse) {
      return;
    }

    if (currentOfferResponse.accepted !== null) {
      setIsAcceptedChecked(currentOfferResponse.accepted);
    }

    setRating(
      currentOfferResponse.correct === true
        ? "correct"
        : currentOfferResponse.correct === false
        ? "incorrect"
        : null
    );
    setIncorrectReason(currentOfferResponse.incorrectReason ?? "");
    form.setFieldsValue({
      ...currentOfferResponse,
      accepted:
        currentOfferResponse.accepted !== null
          ? currentOfferResponse.accepted
          : false,
    });
    actions.getRequestById(currentOfferResponse.offerRequestId);
  }, [currentOfferResponse]);

  const handleFileDownload = () => {
    if (offerResponseId) {
      actions.downloadFile(
        offerResponseId,
        getFileName(currentOfferResponse?.filePath)
      );
    }
  };

  // Step 4 - Rate offer
  const rateOffer = () => {
    if (!offerResponseId || !rating) {
      return;
    }
    actions.rateOfferResponse(
      offerResponseId,
      rating === "correct",
      rating === "incorrect" ? incorrectReason : null
    );
  };

  // Step 5 - Update offer
  const onFinish = (values: ResponseDetailsFormType) => {
    if (
      values.leaseAgreementNumber === "" ||
      values.leaseAgreementNumber === null
    )
      delete values.leaseAgreementNumber;
    if (offerResponseId) {
      actions.updateOfferResponse(offerResponseId, values);
    }
  };

  // Step 6 - Generate order
  const generateOrder = () => {
    if (offerResponseId) {
      actions.generateOrder(
        offerResponseId,
        getFileName(currentOfferResponse?.filePath)
      );
    }
  };

  // Step 8 - Generate invoice
  const generateInvoice = () => {
    if (offerResponseId) {
      actions.generateInvoice(offerResponseId);
    }
  };

  return (
    <div className="px-8 py-4">
      <Typography className="text-xl font-bold text-center mb-6 text-secondary">
        PONUDA ZA ZAHTJEV ZAHTJEV:
        <span className="text-offer-blue ml-2">
          {currentOfferRequest?.name}
        </span>
      </Typography>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <div className="flex flex-col">
            <div className="flex flex-col sm:flex-row my-2">
              <Typography className="w-full sm:w-1/3 sm:text-right pr-4">
                Osiguravajuće društvo:
              </Typography>
              <Typography className="w-full sm:w-2/3">
                {currentOfferRequest?.offerResponse?.find(
                  (response: InsuranceOfferResponseType) =>
                    response.insurance.id === currentOfferResponse?.insuranceId
                )?.insurance.name || ""}
              </Typography>
            </div>

            <div className="flex flex-col sm:flex-row my-2">
              <Typography className="w-full sm:w-1/3 sm:text-right pr-4">
                Ko je odgovorio:
              </Typography>
              <div className="w-full sm:w-2/3">
                {currentOfferRequest?.offerResponse?.map(
                  (response: InsuranceOfferResponseType) => (
                    <Typography key={response.insurance.id}>
                      {response.insurance.name}
                    </Typography>
                  )
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row my-2">
              <Typography className="w-full sm:w-1/3 sm:text-right pr-4">
                Prilog:
              </Typography>
              <div className="flex items-center w-full sm:w-2/3 bg-light-gray">
                <PaperClipOutlined className="text-black opacity-45 mr-2" />
                <div
                  className="text-primary cursor-pointer"
                  onClick={handleFileDownload}
                >
                  {getFileName(currentOfferResponse?.filePath)}
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row my-2">
              <Typography className="w-full sm:w-1/3 sm:text-right pr-4">
                Premija AK:
              </Typography>
              <Typography className="w-full sm:w-2/3">
                {currentOfferResponse?.premiumAO}
              </Typography>
            </div>

            <div className="flex flex-col sm:flex-row my-2">
              <Typography className="w-full sm:w-1/3 sm:text-right pr-4">
                Premija AO:
              </Typography>
              <Typography className="w-full sm:w-2/3">
                {currentOfferResponse?.premiumAO}
              </Typography>
            </div>

            <div className="flex flex-col sm:flex-row my-2">
              <Typography className="w-full sm:w-1/3 sm:text-right pr-4">
                Full troškovi registracije:
              </Typography>
              <Typography className="w-full sm:w-2/3">
                {currentOfferResponse?.fullRegistrationCost}
              </Typography>
            </div>

            <div className="flex flex-col sm:flex-row my-2">
              <Typography className="w-full sm:w-1/3 sm:text-right pr-4">
                Napomena:
              </Typography>
              <Typography className="w-full sm:w-2/3">
                {currentOfferResponse?.note}
              </Typography>
            </div>
            {loggedUser?.role === ROLES.S_PREMIUM_ADMIN &&
              !currentOfferResponse?.accepted && (
                <Button
                  type="primary"
                  htmlType="submit"
                  onClick={rateOffer}
                  disabled={!rating}
                  className="bg-primary ml-0 md:ml-16 my-6 font-medium w-full md:w-[132px] rounded-sm"
                >
                  POŠALJI
                </Button>
              )}

            {isLeasingRole &&
              currentOfferResponse?.correct && (
                <Button
                  type="primary"
                  htmlType="button"
                  onClick={() => form.submit()}
                  className="bg-primary ml-0 md:ml-16 my-6 font-medium w-full md:w-[132px] rounded-sm text-white"
                  disabled={currentOfferResponse?.accepted}
                >
                  DOPUNI
                </Button>
              )}

            <div className="flex flex-col ml-0 xl:ml-12 lg:flex-row my-4 lg:mt-32 space-y-4 lg:space-y-0 lg:space-x-4">
              {loggedUser?.role === ROLES.S_PREMIUM_ADMIN &&
                currentOfferResponse?.accepted && (
                  <>
                    <Button
                      type="default"
                      className="bg-estimate-success w-full md:w-auto rounded-sm text-white"
                      icon={<FilePdfOutlined />}
                      onClick={generateOrder}
                    >
                      KREIRAJ NALOG
                    </Button>
                    {currentOfferResponse?.certifiedOrderFilePath && (
                      <Button
                        type="default"
                        className="bg-secondary w-full md:w-auto rounded-sm text-white"
                        icon={<FilePdfOutlined />}
                        onClick={generateInvoice}
                      >
                        KREIRAJ PREDRAČUN
                      </Button>
                    )}
                  </>
                )}
            </div>
          </div>
        </Col>
        <Col xs={24} md={12} className="flex justify-center md:justify-start">
          <div className="text-center md:text-left">
            <div>
              <Typography className="text-base font-bold text-secondary mb-2">
                OCJENA PONUDE
              </Typography>
              <Radio.Group
                value={rating}
                onChange={(e) => setRating(e.target.value)}
                disabled={currentOfferResponse?.accepted || isLeasingRole}
              >
                <Space direction="vertical">
                  <Radio value="correct">ISPRAVNA PONUDA</Radio>
                  <Radio value="incorrect">NEISPRAVNA PONUDA</Radio>
                </Space>
              </Radio.Group>
              {rating === "incorrect" &&
                (loggedUser?.role === ROLES.S_PREMIUM_ADMIN &&
                !currentOfferResponse?.accepted ? (
                  <Input.TextArea
                    className="mt-3"
                    rows={3}
                    maxLength={1000}
                    placeholder="Razlog neispravnosti (opcionalno)"
                    value={incorrectReason}
                    onChange={(e) => setIncorrectReason(e.target.value)}
                  />
                ) : (
                  currentOfferResponse?.incorrectReason && (
                    <Typography className="mt-3 max-w-xs">
                      Razlog: {currentOfferResponse.incorrectReason}
                    </Typography>
                  )
                ))}
            </div>
            {currentOfferResponse?.accepted ||
            (currentOfferResponse?.correct &&
              isLeasingRole) ? (
              <Form
                {...layout}
                form={form}
                name="basic"
                onFinish={onFinish}
                className="mt-4 bg-dashed-box border border-dashed border-offer-blue py-4 pl-5 pr-10"
                disabled={currentOfferResponse?.accepted}
              >
                <Typography className="text-base font-bold text-secondary mb-2">
                  STATUS PONUDE
                </Typography>
                <Form.Item name="accepted" valuePropName="checked">
                  <Checkbox
                    checked={isAcceptedChecked}
                    onChange={(e: CheckboxChangeEvent) => {
                      setIsAcceptedChecked(e.target.checked);
                    }}
                  >
                    PRIHVAĆENA PONUDA
                  </Checkbox>
                </Form.Item>
                <Form.Item
                  label="Broj leasing ugovora"
                  name="leaseAgreementNumber"
                  wrapperCol={{ span: 14 }}
                  rules={[
                    ({ getFieldValue }) => ({
                      required: getFieldValue("accepted"),
                      message: "Unesite broj leasinga ugovora!",
                    }),
                  ]}
                >
                  <Input />
                </Form.Item>
                <Typography className="text-base font-bold text-secondary mb-2">
                  DETALJI PONUDE
                </Typography>
                <Form.Item
                  label="Godina proizvodnje"
                  name="yearOfManufacture"
                  rules={[
                    {
                      required: true,
                      message: "Unesite godinu proizvodnje!",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  label="Marka vozila"
                  name="vehicleBrand"
                  rules={[
                    {
                      required: true,
                      message: "Unesite marku vozila!",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  label="Tip vozila"
                  name="vehicleType"
                  rules={[
                    {
                      required: true,
                      message: "Unesite tip vozila!",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  label="Broj šasije"
                  name="chassisNumber"
                  rules={[
                    {
                      required: true,
                      message: "Unesite broj šasije!",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Form>
            ) : (
              <div className="mt-4">
                <Typography className="text-base font-bold text-secondary mb-2">
                  STATUS PONUDE
                </Typography>
                <Typography className="text-sm font-bold text-estimate-fail mb-2">
                  NIJE PRIHVAĆENA
                </Typography>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default InsuranceResponseInfo;
